import type { EmailTemplate, EmailTemplateListItem } from '@components/EmailTemplates/types';
import axios from 'axios';

import type { AutomationRuleLike } from './registry';

/**
 * Reads for the email registry.
 *
 * All REST against the Express BFF — the email suite has never used `fetchGraphQLApiData`, which is
 * the entity-workspace idiom for the GraphQL service.
 */

export interface TemplateStat {
  templateKey: string;
  sent: number;
  opened: number;
  clicked: number;
  /** 0–1, not 0–100. See `formatRateFraction`. */
  openRate: number;
  clickRate: number;
}

export interface TrendBucket {
  /** ISO yyyy-MM-dd. */
  bucketStart: string;
  sent: number;
  opened: number;
  clicked: number;
}

export interface FireLogEntry {
  id: string;
  ruleId?: string;
  userId?: string;
  userType?: string;
  templateKey?: string;
  firedAt?: string;
  /** SUCCESS | SKIPPED | FAILED. Null on rows written before the field existed — those are sends. */
  result?: string | null;
  reason?: string | null;
}

export interface SendRecord {
  id: string;
  userEmail?: string;
  userType?: string;
  templateKey?: string;
  sentAt?: string;
  openCount: number;
  clickCount: number;
}

export interface DripSequence {
  campaignType: string;
  name: string;
  audience: string;
  stepCount: number;
  steps: { step: number; dayOffset: number; templateKey: string }[];
}

export interface DripSequenceStats {
  total: number;
  byStatus: Record<string, number>;
  activeByStep: Record<string, number>;
}

/**
 * Everything the list needs, in three calls.
 *
 * Rules are fetched whole (`size=200` against 41 rows) rather than filtered server-side: that
 * endpoint applies its `audience`/`status` filters AFTER paginating and reports counts from an
 * unfiltered `count()`, so a filtered page is "the matches within the first N", with wrong totals.
 * Fetching everything sidesteps it and is what the existing Templates tab already does.
 *
 * Analytics is ONE call for all templates. That service scans the whole 90-day collection in memory
 * per request, so a call per row would re-scan it 75 times.
 */
export const fetchRegistrySources = async (periodDays = 30) => {
  const [templates, rules, analytics] = await Promise.all([
    axios.get<EmailTemplateListItem[]>('/api/email-templates'),
    axios.get<{ content: AutomationRuleLike[] }>('/api/automation-rules', {
      params: { page: 0, size: 200 },
    }),
    axios.get<{ byTemplate: TemplateStat[] }>('/api/email-analytics', {
      params: { periodDays },
    }),
  ]);
  return {
    templates: templates.data ?? [],
    rules: rules.data?.content ?? [],
    stats: analytics.data?.byTemplate ?? [],
  };
};

export const fetchTemplate = async (templateKey: string) =>
  (await axios.get<EmailTemplate>(`/api/email-templates/${encodeURIComponent(templateKey)}`)).data;

export const fetchTrend = async (templateKey: string, periodDays = 90, granularity = 'week') =>
  (
    await axios.get<TrendBucket[]>('/api/email-analytics/trend', {
      params: { templateKey, periodDays, granularity },
    })
  ).data ?? [];

export const fetchFireLog = async (templateKey: string, size = 10) =>
  (
    await axios.get<FireLogEntry[]>('/api/automation-rules/fire-log', {
      params: { templateKey, size },
    })
  ).data ?? [];

/**
 * Recent sends for one template.
 *
 * The endpoint's templateKey filter is a case-insensitive SUBSTRING match, so `employer-drip-1`
 * also returns `employer-drip-10`'s rows. Filtering exactly here is not defensive tidiness: without
 * it the table under the heading "recent sends of this email" shows another email's sends.
 */
export const fetchRecentSends = async (templateKey: string, size = 20) => {
  const { data } = await axios.get<{ content: SendRecord[] }>('/api/email-audit', {
    params: { page: 0, size, periodDays: 90, templateKey },
  });
  return (data?.content ?? []).filter((r) => r.templateKey === templateKey);
};

export const fetchDripSequences = async () =>
  (
    await axios.get<{ sequences: DripSequence[]; stats: Record<string, DripSequenceStats> }>(
      '/api/drip-campaigns/sequences',
    )
  ).data ?? { sequences: [], stats: {} };

/**
 * Pause or resume a rule by its document id. The same BFF endpoints the Automation rules tab
 * uses, so a rule paused here reads as paused there. Returns the updated rule, or null when the
 * backend answered without one.
 */
export const setRulePaused = async (id: string, paused: boolean) =>
  (
    await axios.post<AutomationRuleLike | null>(
      `/api/automation-rules/${encodeURIComponent(id)}/${paused ? 'pause' : 'resume'}`,
    )
  ).data ?? null;
